import { useState } from "react";
import { Link } from "react-router-dom";
import { AtSign, X } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Card, CardContent } from "@/ui/components/ui/card";
import { ListContainer, ListItem } from "@/ui/components/shared/ListItem";
import { UserProfilePicture } from "@/ui/components/UserProfilePicture";
import { ProfilePhotoCropDialog } from "@/ui/components/ProfilePhotoCropDialog";
import { useActions, useAppState } from "@/state/legacy_module_api";
import { ShowMyQrContent } from "@/features/contacts/screens/ShowMyQrScreen";

const ProfilePanel = () => {
  const { myUserId, myUsername, myDiscriminator, myHandle, myUser, profileRenderRef, appId } =
    useAppState();
  const { setDisplayPhoto, uploadDisplayPhotoBlob } = useActions();
  const { handleDeleteMyDisplayPhoto } = useActions().user;
  const [refreshKey, setRefreshKey] = useState(0);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropMimeType, setCropMimeType] = useState<string | undefined>(undefined);

  const openCropper = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      setCropMimeType(file.type);
      setCropSrc(dataUrl);
    };
    input.click();
  };

  const handleCroppedSave = async (blob: Blob) => {
    if (uploadDisplayPhotoBlob) {
      await uploadDisplayPhotoBlob(blob, cropMimeType);
      setRefreshKey((prev) => prev + 1);
    } else {
      await setDisplayPhoto();
    }
  };

  const handleDeletePhoto = async () => {
    await handleDeleteMyDisplayPhoto();
  };

  return (
    <div className="h-full overflow-auto space-y-4 p-4">
      <Card>
        <CardContent className="pt-6">
          <h2 className="text-xl font-semibold mb-4">Profile</h2>

          {/* Profile Photo Section */}
          <div className="flex flex-col items-center justify-center py-4">
            <div className="relative">
              <UserProfilePicture
                key={refreshKey + profileRenderRef}
                user={myUser}
                width={128}
              />

              {myUser?.displayPhoto && (
                <button
                  onClick={handleDeletePhoto}
                  className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                >
                  <X className="w-4 h-4 text-black" />
                </button>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <Button
                onClick={openCropper}
                size="sm"
              >
                Edit Photo
              </Button>
            </div>
          </div>
          <ProfilePhotoCropDialog
            open={!!cropSrc}
            imageSrc={cropSrc || ""}
            mimeType={cropMimeType}
            onClose={() => setCropSrc(null)}
            onSave={handleCroppedSave}
          />
        </CardContent>
      </Card>

      <ShowMyQrContent
        fullHandle={myHandle}
        username={myUsername}
        discriminator={myDiscriminator}
      />

      <Card>
        <CardContent className="pt-6">
          <h4 className="font-medium mb-3">Account Info</h4>
          <ListContainer>
            {myUsername ? (
              <ListItem
                icon={<AtSign className="w-5 h-5" />}
                title={myHandle || myUsername}
                subtitle="Username"
              />
            ) : (
              <Link to="/create-user-view">
                <ListItem
                  icon={<AtSign className="w-5 h-5" />}
                  title="Please create a user"
                  subtitle="Username"
                  showChevron
                />
              </Link>
            )}
            <ListItem
              icon={<AtSign className="w-5 h-5" />}
              title={myUserId || "Unknown"}
              subtitle="User ID"
            />
            <ListItem
              icon={<AtSign className="w-5 h-5" />}
              title={appId || "Unknown"}
              subtitle="App ID"
            />
          </ListContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePanel;
